/**
 * DeepDiveDrawer - Slide-in side panel for full adversarial analysis.
 *
 * Supports minimize (keeps SSE alive), done actions, and history viewing.
 * Wraps FinancialLogicTree (streaming) and FinancialAnalysisPanel (kill sheet).
 */

import { useEffect, useCallback } from 'react';
import { X, ChevronRight, RotateCcw } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useFinancialStream } from '@/hooks/useFinancialStream';
import { FinancialLogicTree } from '@/components/verdict/FinancialLogicTree';
import { FinancialAnalysisPanel } from '@/components/verdict/FinancialAnalysisPanel';
import type { Position } from '@/types/portfolio';

export type DrawerMode = 'closed' | 'open' | 'minimized';
export type AnalysisPhase = 'idle' | 'running' | 'done' | 'error';

interface DeepDiveDrawerProps {
  drawerMode: DrawerMode;
  onClose: () => void;
  onMinimize: () => void;
  onAnalysisComplete: (killSheet: any, snapshot: any) => void;
  onPhaseChange: (phase: AnalysisPhase) => void;
  ticker: string;
  position?: Position;
  /** When set, display a saved result in read-only mode */
  savedResult?: { killSheet: any; snapshot: any } | null;
}

export function DeepDiveDrawer({
  drawerMode,
  onClose,
  onMinimize,
  onAnalysisComplete,
  onPhaseChange,
  ticker,
  position,
  savedResult,
}: DeepDiveDrawerProps) {
  const {
    state,
    isConnected,
    isConnecting,
    error,
    startAnalysis,
    disconnect,
    reset,
  } = useFinancialStream({
    onError: (err) => console.error('Deep dive error:', err),
  });

  const isOpen = drawerMode === 'open';
  const isDone = state.phase === 'concluded';
  const isViewingSaved = !!savedResult;

  // Start analysis when drawer opens with a new ticker (not when viewing saved result)
  useEffect(() => {
    if (isOpen && ticker && !isViewingSaved) {
      reset();
      startAnalysis(ticker, 'fast', undefined, { portfolioContext: true });
      onPhaseChange('running');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, ticker, isViewingSaved]);

  // Track phase changes
  useEffect(() => {
    if (isViewingSaved) return;
    if (isDone) {
      onPhaseChange('done');
      if (state.killSheet) {
        onAnalysisComplete(state.killSheet, state.hardDataSnapshot);
      }
    } else if (error) {
      onPhaseChange('error');
    } else if (isConnected || isConnecting) {
      onPhaseChange('running');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDone, error, isConnected, isConnecting, isViewingSaved]);

  // Close on Escape — minimize instead of close when running/done
  useEffect(() => {
    if (drawerMode !== 'open') return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (isDone || isConnected || isConnecting) {
          onMinimize();
        } else {
          onClose();
        }
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [drawerMode, isDone, isConnected, isConnecting, onMinimize, onClose]);

  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  // Backdrop click — minimize if running/done, close if idle
  const handleBackdropClick = useCallback(() => {
    if (isDone || isConnected || isConnecting || isViewingSaved) {
      onMinimize();
    } else {
      onClose();
    }
  }, [isDone, isConnected, isConnecting, isViewingSaved, onMinimize, onClose]);

  const handleClose = useCallback(() => {
    disconnect();
    onClose();
  }, [disconnect, onClose]);

  const handleNewAnalysis = useCallback(() => {
    // This will be handled by the parent — close current and allow new ticker selection
    disconnect();
    onClose();
  }, [disconnect, onClose]);

  // Determine what to show
  const showSavedResult = isViewingSaved && savedResult;
  const showLiveStream = !isViewingSaved;

  return (
    <>
      {/* Backdrop */}
      <div
        className={cn(
          'fixed inset-0 z-40 bg-black/50 backdrop-blur-sm transition-opacity duration-300',
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none',
        )}
        onClick={handleBackdropClick}
        aria-hidden="true"
      />

      {/* Drawer panel */}
      <div
        className={cn(
          'fixed inset-y-0 right-0 z-50 flex flex-col',
          'w-full sm:w-[85%] md:w-[70%] lg:w-[60%] xl:w-[55%]',
          'bg-background border-l border-border shadow-2xl',
          'transition-transform duration-300 ease-out',
          isOpen ? 'translate-x-0' : 'translate-x-full',
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
          <div>
            <h2 className="text-lg font-bold text-text-primary">
              Deep Dive: {ticker}
            </h2>
            {position && (
              <p className="text-xs text-text-muted mt-0.5">
                {position.quantity} shares @ ${position.cost_basis.toFixed(2)} avg
              </p>
            )}
            {isViewingSaved && (
              <p className="text-xs text-amber-400/80 mt-0.5">Viewing saved result</p>
            )}
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={onMinimize}
              className="p-2 rounded-lg text-text-muted hover:text-text-primary hover:bg-surface-light transition-colors"
              aria-label="Minimize deep dive"
              title="Minimize"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
            <button
              onClick={handleClose}
              className="p-2 rounded-lg text-text-muted hover:text-text-primary hover:bg-surface-light transition-colors"
              aria-label="Close deep dive"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {error && !isViewingSaved && (
            <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm mb-4">
              {error}
            </div>
          )}

          {/* Saved result view */}
          {showSavedResult && savedResult.killSheet && (
            <FinancialAnalysisPanel
              killSheet={savedResult.killSheet}
              anchoredSnapshot={savedResult.snapshot}
            />
          )}

          {/* Live streaming progress tree */}
          {showLiveStream && !isDone && (
            <FinancialLogicTree
              state={state}
              query={`Deep dive analysis for ${ticker}`}
              hideActions
            />
          )}

          {/* Live final results */}
          {showLiveStream && isDone && state.killSheet && (
            <FinancialAnalysisPanel
              killSheet={state.killSheet}
              anchoredSnapshot={state.hardDataSnapshot}
              convergenceTrajectory={state.convergenceTrajectory}
              initialDivergence={state.initialDivergence}
              echoChamberedDetected={state.echoChamberedDetected}
            />
          )}

          {/* Loading indicator when connecting */}
          {showLiveStream && isConnecting && !isConnected && !error && state.phase === 'idle' && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
                <p className="text-sm text-text-muted mt-3">Starting analysis...</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer actions when done */}
        {(isDone || isViewingSaved) && (
          <div className="px-6 py-3 border-t border-border shrink-0 flex items-center justify-end gap-2">
            <button
              onClick={handleNewAnalysis}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-primary bg-primary/10 hover:bg-primary/20 transition-colors"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              New Analysis
            </button>
            <button
              onClick={handleClose}
              className="px-4 py-2 rounded-lg text-sm font-medium text-text-muted hover:text-text-primary hover:bg-surface-light transition-colors"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </>
  );
}
